import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, CircularProgress, MenuItem, TextField } from "@mui/material";
import Topbar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import EmptyState from "../../../../../../component/EmptyState";
import InlineMessage from "../../../../../../component/InlineMessage";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { getJoinRequests, decideJoinRequest } from "../../../../../../services/auth";
import { useLegalUnits } from "../../../../../../services/useOrgData";
import { invalidateOrgEmployees } from "../../../../../../services/globalData";
import { tokens, displayFont } from "../../../../../../theme";

/**
 * People asking to be added to this organisation.
 *
 * Everybody here has an email address on this company's domain — that is the
 * only way a request can be raised — so the question for the administrator is
 * whether they recognise the person, not whether the address is plausible.
 *
 * Approving creates an employee record on the Employee role. It is the same act
 * as adding somebody by hand, reached from the other direction.
 */
const JoinRequests = () => {
  const notify = useNotify();
  const [rows, setRows] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [error, setError] = useState("");
  // Which entity each pending person would join. Only asked for when there is
  // more than one — with a single entity there is nothing to decide.
  const [entityFor, setEntityFor] = useState({});
  // The hook returns { data, loading, error }, not the array.
  const { data: entities = [] } = useLegalUnits();
  const mustChoose = (entities || []).length > 1;

  const load = useCallback(async () => {
    const res = await getJoinRequests();
    if (res?.success) {
      setRows(res.data || []);
      setError("");
    } else {
      setRows([]);
      setError(res?.message || "Could not load requests");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (row, approve) => {
    const legalUnitId = approve && mustChoose ? entityFor[row._id] : undefined;
    if (approve && mustChoose && !legalUnitId) {
      notify.error("Choose which legal entity they join");
      return;
    }
    setDeciding(row._id);
    const res = await decideJoinRequest(row._id, approve, legalUnitId);
    setDeciding(null);
    if (res?.success) {
      notify.success(approve ? `${row.name || row.email} was added` : "Request declined");
      // Approving creates an employee, and the directory is cached for the
      // session — without this the person is added and then does not appear in
      // People until something else happens to clear it, which reads as the
      // approval having failed.
      if (approve) invalidateOrgEmployees();
      load();
    } else {
      notify.error(res?.message || "Could not save that decision");
    }
  };

  return (
    <>
      <Topbar title="Requests to join" />
      <MainBox>
        {error && (
          <InlineMessage tone="danger" title="Could not load requests" sx={{ marginBottom: "1.25rem" }}>
            {error}
          </InlineMessage>
        )}

        {mustChoose && rows?.length > 0 && (
          <InlineMessage tone="info" sx={{ marginBottom: "1.25rem" }}>
            Choose the legal entity each person joins. It settles which company
            employs them, so it decides their payroll and their statutory
            registrations — not something to correct later.
          </InlineMessage>
        )}

        {rows === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nobody is waiting"
            body="When somebody with an email address on your company's domain asks to be added, they appear here."
          />
        ) : (
          <Stack spacing={1.25}>
            {rows.map((row) => (
              <Stack
                key={row._id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: "10px",
                  padding: "1rem 1.25rem",
                  background: tokens.card,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontFamily: displayFont, fontWeight: 600, fontSize: "1rem", color: tokens.text }}>
                    {row.name || row.email}
                  </Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, marginTop: "0.25rem" }}>
                    {row.email}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  {mustChoose && (
                    <TextField
                      select
                      size="small"
                      label="Legal entity"
                      value={entityFor[row._id] || ""}
                      onChange={(e) =>
                        setEntityFor((m) => ({ ...m, [row._id]: e.target.value }))
                      }
                      sx={{ minWidth: "12rem" }}
                    >
                      {(entities || []).map((entity) => (
                        <MenuItem key={entity._id} value={entity._id}>
                          {entity.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <BtnSecondary
                    disabled={deciding === row._id}
                    onClick={() => decide(row, false)}
                    sx={{ borderRadius: "8px" }}
                  >
                    Decline
                  </BtnSecondary>
                  <BtnPrimary
                    disabled={deciding === row._id}
                    onClick={() => decide(row, true)}
                    sx={{ borderRadius: "8px", minWidth: "6rem" }}
                  >
                    {deciding === row._id ? "Saving…" : "Add them"}
                  </BtnPrimary>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </MainBox>
    </>
  );
};

export default JoinRequests;
